import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import dayjs from "dayjs";
import { suggestTutors } from "../../utils/coverBlocks";
import {
  createCoverGroup,
  DEFAULT_TEMPLATES,
  errorMessage,
  fmtDay,
  fmtRange,
  pickRandom,
  renderTemplate,
  summariseSessions,
} from "../../utils/coversApi";

const TUTOR_DEFAULT = "Hi {firstName}, I can't make these sessions. Are you able to cover?";

const CreateCoverGroupDialog = ({ open, onClose, sessions, tutors, weekLessons, templates, isManager = true, onCreated }) => {
  // Managers pick from the shared templates; a tutor writes their own note.
  const templateList = isManager ? (templates?.length ? templates : DEFAULT_TEMPLATES) : [TUTOR_DEFAULT];
  const originalTutorIds = [...new Set(sessions.map((s) => s.tutorId || ""))];
  const originalTutorNames = [...new Set(sessions.map((s) => s.tutorName || "Unassigned"))];
  const mixed = originalTutorIds.length > 1;
  const originalTutorName = originalTutorNames.join(", ") || "Unassigned";

  const candidates = useMemo(
    () =>
      open
        ? suggestTutors({
            sessions,
            tutors: tutors.filter((t) => t.role !== "Minion"),
            otherLessons: weekLessons,
            originalTutorIds,
            hideSensitiveReasons: !isManager,
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, sessions, tutors, weekLessons]
  );

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [message, setMessage] = useState("");
  const [showOthers, setShowOthers] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(candidates.filter((c) => c.suggested).map((c) => c.id)));
      setMessage(pickRandom(templateList));
      setShowOthers(false);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidates]);

  const previewTutor = candidates.find((c) => selectedIds.has(c.id)) || candidates[0];
  const previewVars = {
    firstName: previewTutor?.name?.split(" ")[0] || "there",
    originalTutor: originalTutorName || "",
    sessions: summariseSessions(sessions),
    count: sessions.length,
    day: sessions[0] ? dayjs(sessions[0].startDateTime).format("dddd D MMMM") : "",
  };

  const suggested = candidates.filter((c) => c.suggested);
  const others = candidates.filter((c) => !c.suggested);
  const otherSelected = others.filter((c) => selectedIds.has(c.id));

  const toggleId = (id, on) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  const handleSend = async () => {
    setSending(true);
    setError("");
    try {
      await createCoverGroup({
        lessonIds: sessions.map((s) => s.id),
        tutorIds: [...selectedIds],
        message: message.trim(),
      });
      onCreated();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const sorted = [...sessions].sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Create cover request</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Sessions for {originalTutorName}
            </Typography>
            <List dense disablePadding>
              {sorted.map((s) => (
                <ListItem key={s.id} disableGutters>
                  <ListItemText
                    primary={`${fmtDay(s.startDateTime)} · ${fmtRange(s.startDateTime, s.endDateTime)} · ${
                      s.subjectGroupName || "Lesson"
                    }${mixed ? ` · ${s.tutorName || "Unassigned"}` : ""}`}
                    secondary={`${(s.studentNames || []).join(", ") || "No students"}${
                      s.locationName ? ` · ${s.locationName}` : ""
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Invite tutors{" "}
              <Typography component="span" color="text.secondary">
                ({selectedIds.size} selected)
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Suggested tutors have the capability for every session, no blocked students, and no
              clashing lesson. Regular availability is not checked.
            </Typography>

            {suggested.length === 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                No tutors match every session automatically. Add tutors manually below.
              </Alert>
            )}

            <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1}>
              {suggested.map((c) => (
                <FormControlLabel
                  key={c.id}
                  sx={{ mr: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(c.id)}
                      onChange={(e) => toggleId(c.id, e.target.checked)}
                    />
                  }
                  label={c.name}
                />
              ))}
            </Stack>

            {otherSelected.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1}>
                {otherSelected.map((c) => (
                  <Chip
                    key={c.id}
                    label={c.name}
                    color="warning"
                    variant="outlined"
                    onDelete={() => toggleId(c.id, false)}
                    title={c.reasons.join("; ")}
                  />
                ))}
              </Stack>
            )}

            <Autocomplete
              size="small"
              options={others.filter((c) => !selectedIds.has(c.id))}
              getOptionLabel={(o) => o.name}
              value={null}
              onChange={(_, val) => val && toggleId(val.id, true)}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.reasons.join(" · ")}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Add another tutor" placeholder="Type a name" />
              )}
            />

            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setShowOthers((v) => !v)}
              endIcon={showOthers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showOthers ? "Hide" : "Show"} why others aren't suggested ({others.length})
            </Button>
            <Collapse in={showOthers}>
              <List dense>
                {others.map((c) => (
                  <ListItem key={c.id} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        size="small"
                        edge="start"
                        checked={selectedIds.has(c.id)}
                        onChange={(e) => toggleId(c.id, e.target.checked)}
                      />
                    </ListItemIcon>
                    <ListItemText primary={c.name} secondary={c.reasons.join(" · ")} />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography variant="h5" fontWeight="bold">
                {isManager ? "Message" : "Your message"}
              </Typography>
              {templateList.length > 1 && (
                <Button
                  size="small"
                  startIcon={<ShuffleIcon />}
                  onClick={() => setMessage((m) => pickRandom(templateList, m))}
                >
                  Try another template
                </Button>
              )}
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputProps={{ "data-testid": "cover-message" }}
              helperText={
                isManager
                  ? "Sent to each invited tutor in their Live Senior Tutor chat, followed by the request card. Placeholders like {firstName} are filled in for each tutor."
                  : "Sent to each tutor as a direct message from you, followed by the request card. {firstName} is filled in for each tutor."
              }
            />
            {previewTutor && (
              <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary">
                  Preview for {previewTutor.name}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }} data-testid="cover-preview">
                  {renderTemplate(message, previewVars)}
                </Typography>
              </Box>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || selectedIds.size === 0 || sessions.length === 0}
        >
          {sending ? "Sending…" : `Send to ${selectedIds.size} tutor${selectedIds.size === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCoverGroupDialog;
